import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

// MUS-450 - Bluetooth (BLE) MIDI keyboards on Android.
//
// Android never lists a Bluetooth MIDI keyboard by itself: once the user has paired it in
// Android's Bluetooth settings, the app has to open it. The native side (MainActivity and
// MuseFlowBluetoothMidiPlugin) does that and feeds the keyboard into the same MIDIReceived
// events as a USB keyboard, so notes, the connected state and scoring need nothing new
// here. This module only drives the connect steps: permission, choosing the paired
// keyboard, connect and disconnect.

export const ANDROID_BLUETOOTH_MIDI_PLUGIN = 'MuseFlowBluetoothMidi';

export type BluetoothPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

export type BluetoothMidiStatus = {
  supported: boolean;
  bluetoothOn: boolean;
  permission: BluetoothPermissionState;
  connectedName: string | null;
};

export type PairedBluetoothKeyboard = {
  id: string;
  name: string;
  /** The device advertises the Bluetooth MIDI service. */
  midi: boolean;
};

export type BluetoothMidiConnectionChange = {
  connected: boolean;
  name: string | null;
};

export interface AndroidBluetoothMidiPlugin {
  getStatus(): Promise<BluetoothMidiStatus>;
  requestPermission(): Promise<{ permission: BluetoothPermissionState }>;
  listPairedKeyboards(): Promise<{ keyboards: PairedBluetoothKeyboard[] }>;
  connect(options: { id: string }): Promise<{ name: string }>;
  disconnect(): Promise<void>;
  openAppSettings(): Promise<void>;
  openBluetoothSettings(): Promise<void>;
  addListener(
    eventName: 'connectionChanged',
    handler: (change: BluetoothMidiConnectionChange) => void,
  ): Promise<PluginListenerHandle>;
}

export const AndroidBluetoothMidi = registerPlugin<AndroidBluetoothMidiPlugin>(
  ANDROID_BLUETOOTH_MIDI_PLUGIN,
);

export type AndroidBluetoothMidiEnv = {
  isNative: boolean;
  platform: string;
  pluginAvailable: boolean;
};

/** Pure predicate: offer the Android Bluetooth keyboard connect steps. */
export const shouldOfferAndroidBluetoothMidi = (env: AndroidBluetoothMidiEnv): boolean =>
  env.isNative && env.platform === 'android' && env.pluginAvailable;

export const isAndroidBluetoothMidiAvailable = (): boolean =>
  shouldOfferAndroidBluetoothMidi({
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    pluginAvailable: Capacitor.isPluginAvailable(ANDROID_BLUETOOTH_MIDI_PLUGIN),
  });

export type BluetoothKeyboardStep =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'unsupported' }
  | { kind: 'bluetooth-off' }
  | { kind: 'permission-denied' }
  | { kind: 'no-keyboards' }
  | { kind: 'choose'; keyboards: PairedBluetoothKeyboard[] }
  | { kind: 'connecting'; name: string }
  | { kind: 'connected'; name: string }
  | { kind: 'failed'; name: string }
  | { kind: 'lost'; name: string }
  | { kind: 'error' };

/** Keyboards that advertise the MIDI service first; the native list is already sorted. */
export const stepForPairedKeyboards = (
  keyboards: PairedBluetoothKeyboard[],
): BluetoothKeyboardStep =>
  keyboards.length === 0
    ? { kind: 'no-keyboards' }
    : {
        kind: 'choose',
        keyboards: [
          ...keyboards.filter((keyboard) => keyboard.midi),
          ...keyboards.filter((keyboard) => !keyboard.midi),
        ],
      };

/**
 * From a tap on "Connect Bluetooth keyboard" to the list of paired keyboards. Asks for the
 * Bluetooth permission only when it is not granted yet; a refusal ends on
 * 'permission-denied', from which the user can try again or open the app's settings.
 */
export const findPairedBluetoothKeyboards = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const status = await plugin.getStatus();
    if (!status.supported) return { kind: 'unsupported' };
    if (!status.bluetoothOn) return { kind: 'bluetooth-off' };

    const permission =
      status.permission === 'granted'
        ? status.permission
        : (await plugin.requestPermission()).permission;
    if (permission !== 'granted') return { kind: 'permission-denied' };

    const { keyboards } = await plugin.listPairedKeyboards();
    return stepForPairedKeyboards(keyboards);
  } catch {
    return { kind: 'error' };
  }
};

export const connectPairedBluetoothKeyboard = async (
  keyboard: PairedBluetoothKeyboard,
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const { name } = await plugin.connect({ id: keyboard.id });
    return { kind: 'connected', name: name || keyboard.name };
  } catch {
    return { kind: 'failed', name: keyboard.name };
  }
};

/** The step to show when the settings page opens: connected if a keyboard already is. */
export const initialBluetoothKeyboardStep = async (
  plugin: AndroidBluetoothMidiPlugin = AndroidBluetoothMidi,
): Promise<BluetoothKeyboardStep> => {
  try {
    const status = await plugin.getStatus();
    return status.connectedName
      ? { kind: 'connected', name: status.connectedName }
      : { kind: 'idle' };
  } catch {
    return { kind: 'idle' };
  }
};

/** A native connection change while the page is open. */
export const stepAfterConnectionChange = (
  current: BluetoothKeyboardStep,
  change: BluetoothMidiConnectionChange,
): BluetoothKeyboardStep => {
  if (change.connected && change.name) return { kind: 'connected', name: change.name };
  if (current.kind === 'connected') return { kind: 'lost', name: current.name };
  return current;
};
